"use client";

import Link from "next/link";
import { useState } from "react";
import { trackEvent } from "@/lib/analytics-events";
import {
  archiveDecisionJourney,
  checkInDecisionJourney,
  type AccountChartSubject,
  type AccountDecisionJourney,
  type AccountReading,
  type DecisionJourneyOutcome,
} from "./account-api";
import { AccountDecisionJourneyForm } from "./AccountDecisionJourneyForm";
import { formatAccountCivilDate } from "./account-format";

export function AccountDecisionJourneys({
  chartSubjects,
  journeys,
  onRefresh,
  readings,
  token,
  verified,
}: {
  chartSubjects: AccountChartSubject[];
  journeys: AccountDecisionJourney[];
  onRefresh: () => Promise<void>;
  readings: AccountReading[];
  token: string;
  verified: boolean;
}) {
  if (!verified || readings.length === 0) return null;
  const active = journeys.filter((journey) => journey.status === "active");
  const completed = journeys.filter((journey) => journey.status === "completed");
  return (
    <section
      aria-labelledby="decision-journeys-heading"
      className="mb-6 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-raised)] p-5 shadow-[var(--shadow-elevation-low)]"
    >
      <p className="text-xs font-semibold uppercase tracking-wider text-[var(--color-accent)]">
        From reading to real decision
      </p>
      <h2 id="decision-journeys-heading" className="mt-1 font-serif text-2xl">
        Career & marriage journeys
      </h2>
      <p className="mt-2 text-sm leading-6 text-[var(--color-text-sub)]">
        Keep one decision moving across clarity, preparation, action and a real-world outcome. Your
        chart stays the context; the journey records what you actually did.
      </p>
      <div className="mt-5 grid gap-4">
        {active.map((journey) => (
          <JourneyCard
            key={journey.journey_id}
            journey={journey}
            token={token}
            onRefresh={onRefresh}
          />
        ))}
      </div>
      <details
        className="mt-5 rounded-lg border border-[var(--color-border)] p-4"
        open={!active.length}
      >
        <summary className="cursor-pointer text-sm font-semibold text-[var(--color-text-main)]">
          {active.length ? "Start or update another journey" : "Start a decision journey"}
        </summary>
        <AccountDecisionJourneyForm
          chartSubjects={chartSubjects}
          readings={readings}
          token={token}
          onSaved={onRefresh}
        />
      </details>
      {completed.length ? (
        <details className="mt-4">
          <summary className="cursor-pointer text-sm font-semibold text-[var(--color-text-sub)]">
            Completed outcomes ({completed.length})
          </summary>
          <div className="mt-3 grid gap-3">
            {completed.map((journey) => (
              <JourneyCard
                key={journey.journey_id}
                journey={journey}
                token={token}
                onRefresh={onRefresh}
              />
            ))}
          </div>
        </details>
      ) : null}
    </section>
  );
}

function JourneyCard({
  journey,
  onRefresh,
  token,
}: {
  journey: AccountDecisionJourney;
  onRefresh: () => Promise<void>;
  token: string;
}) {
  return (
    <article className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-[var(--color-accent)]">
            {journey.journey_label}
          </p>
          <h3 className="mt-1 font-serif text-xl">{journey.goal_label}</h3>
          <p className="mt-1 text-sm text-[var(--color-text-sub)]">{journey.stage_label}</p>
        </div>
        {journey.target_date ? (
          <span className="rounded-full bg-[var(--color-accent-light)] px-3 py-1 text-xs font-semibold text-[var(--color-accent)]">
            Target {formatAccountCivilDate(journey.target_date)}
          </span>
        ) : null}
      </div>
      <progress
        className="mt-4 block h-2 w-full appearance-none overflow-hidden rounded-full bg-[var(--color-border)] [&::-moz-progress-bar]:bg-[var(--color-accent)] [&::-webkit-progress-bar]:bg-[var(--color-border)] [&::-webkit-progress-value]:bg-[var(--color-accent)]"
        aria-label={`${journey.journey_label} progress`}
        value={journey.progress_percent}
        max={100}
      >
        {journey.progress_percent}%
      </progress>
      <p className="mt-4 text-sm font-semibold text-[var(--color-text-main)]">
        {journey.next_action_label}
      </p>
      <p className="mt-1 text-sm leading-6 text-[var(--color-text-sub)]">
        {journey.next_action_detail}
      </p>
      {journey.status === "active" ? (
        <JourneyActions journey={journey} token={token} onRefresh={onRefresh} />
      ) : null}
    </article>
  );
}

function JourneyActions({
  journey,
  onRefresh,
  token,
}: {
  journey: AccountDecisionJourney;
  onRefresh: () => Promise<void>;
  token: string;
}) {
  const [outcome, setOutcome] = useState<DecisionJourneyOutcome>("on_track");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function saveOutcome() {
    setBusy(true);
    setError("");
    try {
      await checkInDecisionJourney(token, journey.journey_id, outcome, crypto.randomUUID());
      await onRefresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not save check-in.");
    } finally {
      setBusy(false);
    }
  }

  async function archive() {
    setBusy(true);
    setError("");
    try {
      await archiveDecisionJourney(token, journey.journey_id, crypto.randomUUID());
      await onRefresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not archive journey.");
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 border-t border-[var(--color-border)] pt-4">
      {journey.next_action_url ? (
        <Link
          href={journey.next_action_url}
          onClick={() =>
            trackEvent("journey_action_clicked", {
              journey_action_id: `${journey.journey_id}:${journey.state_version}`,
              reason_code: journey.next_action_key,
              surface: "account_decision_journey",
            })
          }
          className="inline-flex min-h-11 items-center rounded-md bg-[var(--color-accent)] px-4 py-2 text-sm font-semibold text-white"
        >
          Continue with AI Jyotishi
        </Link>
      ) : null}
      <div className="mt-4 flex flex-wrap items-end gap-2">
        <label className="min-w-44 flex-1 text-xs font-semibold text-[var(--color-text-sub)]">
          What happened?
          <select
            value={outcome}
            onChange={(event) => setOutcome(event.target.value as DecisionJourneyOutcome)}
            className="mt-1.5 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-raised)] px-3 py-2 text-sm"
          >
            <option value="on_track">I took the step</option>
            <option value="blocked">I am blocked</option>
            <option value="changed_plan">The plan changed</option>
            <option value="achieved">Goal achieved</option>
            <option value="no_update">No update yet</option>
          </select>
        </label>
        <button
          type="button"
          disabled={busy}
          onClick={() => void saveOutcome()}
          className="min-h-11 rounded-md border border-[var(--color-accent)] px-4 py-2 text-sm font-semibold text-[var(--color-accent)] disabled:opacity-50"
        >
          {busy ? "Saving..." : "Save check-in"}
        </button>
      </div>
      <button
        type="button"
        disabled={busy}
        onClick={() => void archive()}
        className="mt-3 text-xs font-semibold text-[var(--color-text-sub)] underline-offset-4 hover:underline"
      >
        Archive journey
      </button>
      {error ? <p className="mt-2 text-sm text-[var(--color-error-text)]">{error}</p> : null}
    </div>
  );
}
